// Quebra o texto de uma mensagem nas partes que a tela renderiza de forma
// diferente: link clicável, menção destacada e texto comum.
//
// Links são procurados ANTES das menções de propósito: a regex de menção aceita
// espaços e acentos, então uma URL contendo "@" (github.com/@fulano) seria
// engolida como se fosse menção.

export type MessageToken =
  | { type: 'text'; value: string }
  | { type: 'mention'; value: string }
  | { type: 'link'; value: string; href: string };

// Só http(s) e www viram link. Qualquer outro esquema — javascript:, data: —
// fica como texto: o conteúdo vem de quem escreveu a mensagem, e um href
// controlado pelo autor é execução de código no clique de quem lê.
const URL_REGEX = /(https?:\/\/[^\s<>]+|www\.[^\s<>]+)/gi;

// Pontuação que costuma encostar no fim do link e não faz parte dele.
const PONTUACAO_FINAL = /[.,;:!?)\]}'"]+$/;

const LETRA_OU_DIGITO = /[\p{L}\p{N}]/u;
const UMA_PALAVRA = /^[\p{L}\p{N}_]+/u;

// Tamanho da menção que começa no "@" em `inicio`, ou 0 se ali não há menção.
//
// Nomes têm espaço ("Ricardo Lima"), então não dá para saber pelo texto onde a
// menção acaba. Antes a regex aceitava espaços e ia até o fim da frase: a
// mensagem inteira depois do @ ficava destacada. Agora vale o nome conhecido
// mais longo que casar ali; se nenhum casar, só a palavra logo após o @.
function tamanhoDaMencao(texto: string, inicio: number, nomes: string[]): number {
  const anterior = texto[inicio - 1];
  // "joao@empresa.com" não é menção.
  if (anterior !== undefined && LETRA_OU_DIGITO.test(anterior)) return 0;

  const resto = texto.slice(inicio + 1);
  const restoMinusculo = resto.toLowerCase();

  for (const nome of nomes) {
    if (!restoMinusculo.startsWith(nome.toLowerCase())) continue;
    const seguinte = resto[nome.length];
    if (seguinte === undefined || !LETRA_OU_DIGITO.test(seguinte)) return nome.length + 1;
  }

  const palavra = UMA_PALAVRA.exec(resto);
  return palavra ? palavra[0].length + 1 : 0;
}

function separarMencoes(texto: string, nomes: string[]): MessageToken[] {
  const tokens: MessageToken[] = [];
  let ultimoIndice = 0;
  let arroba = texto.indexOf('@');

  while (arroba !== -1) {
    const tamanho = tamanhoDaMencao(texto, arroba, nomes);
    if (tamanho > 0) {
      if (arroba > ultimoIndice) {
        tokens.push({ type: 'text', value: texto.substring(ultimoIndice, arroba) });
      }
      tokens.push({ type: 'mention', value: texto.substring(arroba, arroba + tamanho) });
      ultimoIndice = arroba + tamanho;
    }
    arroba = texto.indexOf('@', Math.max(arroba + 1, ultimoIndice));
  }

  if (ultimoIndice < texto.length) {
    tokens.push({ type: 'text', value: texto.substring(ultimoIndice) });
  }

  return tokens;
}

// `nomesMencionaveis`: nomes de quem pode ser citado (os membros da sala). Com
// eles a menção pega o nome completo; sem eles, só a primeira palavra.
export function splitMessageIntoTokens(
  content: string,
  nomesMencionaveis: string[] = []
): MessageToken[] {
  if (!content) return [];

  // Mais longo primeiro: "Ana Maria" tem de ganhar de "Ana".
  const nomes = nomesMencionaveis
    .map(nome => nome.trim())
    .filter(Boolean)
    .sort((a, b) => b.length - a.length);

  const tokens: MessageToken[] = [];
  let ultimoIndice = 0;
  let encontrado: RegExpExecArray | null;

  URL_REGEX.lastIndex = 0;
  while ((encontrado = URL_REGEX.exec(content)) !== null) {
    const bruto = encontrado[0];
    const semPontuacao = bruto.replace(PONTUACAO_FINAL, '');
    const sobra = bruto.slice(semPontuacao.length);

    if (encontrado.index > ultimoIndice) {
      tokens.push(...separarMencoes(content.substring(ultimoIndice, encontrado.index), nomes));
    }

    tokens.push({
      type: 'link',
      value: semPontuacao,
      href: semPontuacao.toLowerCase().startsWith('www.') ? `https://${semPontuacao}` : semPontuacao,
    });

    if (sobra) {
      tokens.push({ type: 'text', value: sobra });
    }

    ultimoIndice = encontrado.index + bruto.length;
  }

  if (ultimoIndice < content.length) {
    tokens.push(...separarMencoes(content.substring(ultimoIndice), nomes));
  }

  return tokens;
}
